import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { getUserComplianceStatus, submitCompliance, type ComplianceStatus } from "../api/compliance";
import { updateUserPreferences } from "../api/endpoints";
import { navigateTo as navigate } from "../utils/navigation";
import { getErrorMessage } from "../utils/errors";
import Button from "./ui/Button";

/** EU AI Act Art. 50(3): what the AI does with a face photo, shown before the
 *  first analysis rather than only inside the linked consent document. */
export const AI_PRE_ANALYSIS_NOTICE =
  "Kivo uses AI to analyze your face photo and assign it to cosmetic categories such as skin type, skin tone, undertone, hair type and face shape, and to estimate your age and gender. It does not infer health conditions, ethnicity, religion or other sensitive traits.";

interface PhotoConsentGateProps {
  onAccepted?: () => void;
}

function ConsentCheckbox({
  id,
  checked,
  onChange,
  children,
}: {
  id: string;
  checked: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  children: React.ReactNode;
}) {
  return (
    <label htmlFor={id} className="flex items-start gap-3 cursor-pointer">
      <input id={id} type="checkbox" checked={checked} onChange={onChange} className="sr-only" />
      <div
        className="mt-0.5 flex-shrink-0 w-5 h-5 flex items-center justify-center"
        style={{
          borderRadius: 'var(--r-xs)',
          border: `1px solid ${checked ? 'var(--c-kivo-teal)' : 'var(--rule)'}`,
          background: checked ? 'var(--c-kivo-teal)' : 'var(--c-cream-weak)',
          color: checked ? 'var(--c-cream)' : 'transparent',
          transition: 'background var(--t-fast) var(--ease), border-color var(--t-fast) var(--ease)',
        }}
      >
        <motion.svg
          viewBox="0 0 12 10"
          className="w-3 h-3"
          initial={false}
          animate={checked ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.3 }}
          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        >
          <path d="M1 5l3 3 7-7" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        </motion.svg>
      </div>
      <span style={{ fontSize: 'var(--fs-small)', color: 'var(--ink)', lineHeight: 1.5 }} className="select-none">
        {children}
      </span>
    </label>
  );
}

const CONSENT_ITEMS = [
  'Facial photo processing',
  'Biometric data analysis',
  'Secure EU data storage',
  'AI-powered skin insights',
];

export default function PhotoConsentGate({ onAccepted }: PhotoConsentGateProps): React.JSX.Element {
  const [photoConsent, setPhotoConsent] = useState<ComplianceStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [accepted, setAccepted] = useState(false);
  const [aiConsent, setAiConsent] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Keep the latest onAccepted in a ref so the consent check below can run exactly
  // once on mount without depending on the callback's identity. Parents often pass
  // a fresh inline `onAccepted` on every render (e.g. while dragging the crop), and
  // depending on it here would re-run the fetch and flash the full-screen loader.
  const onAcceptedRef = useRef(onAccepted);
  useEffect(() => { onAcceptedRef.current = onAccepted; }, [onAccepted]);

  useEffect(() => {
    async function checkPhotoConsent() {
      try {
        setLoading(true);
        const status = await getUserComplianceStatus();
        const photoConsentStatus = status.statuses.find(s => s.slug === 'photo-consent');
        if (photoConsentStatus) {
          setPhotoConsent(photoConsentStatus);
          if (photoConsentStatus.accepted && !photoConsentStatus.needs_update) {
            onAcceptedRef.current?.();
          }
        } else {
          setError("Photo consent policy not found. Please contact support.");
        }
      } catch (error_) {
        console.error("Failed to check photo consent:", error_);
        setError(getErrorMessage(error_, "Failed to load consent status. Please try again."));
      } finally {
        setLoading(false);
      }
    }
    void checkPhotoConsent();
  }, []);

  const handleAccept = async () => {
    if (!photoConsent) return;
    try {
      setSubmitting(true);
      await submitCompliance(photoConsent.compliance_id);
      if (aiConsent) {
        try { await updateUserPreferences({ photo_ai_consent: true }); } catch { /* optional preference sync */ }
      }
      setSuccess(true);
      setTimeout(() => { onAcceptedRef.current?.(); }, 350);
    } catch (error_) {
      console.error("Failed to submit photo consent:", error_);
      setError(getErrorMessage(error_, "Failed to submit consent. Please try again."));
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center" style={{ background: 'var(--c-cream)' }}>
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full blur-3xl pointer-events-none" style={{ background: 'var(--c-teal-weak)' }} />
        <div className="absolute bottom-1/4 right-1/3 w-64 h-64 rounded-full blur-3xl pointer-events-none" style={{ background: 'rgba(244,168,142,0.10)' }} />
        <div className="relative w-14 h-14 mb-5">
          <svg className="w-full h-full -rotate-90 animate-spin" style={{ animationDuration: '1.4s' }} viewBox="0 0 36 36">
            <circle cx="18" cy="18" r="14" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ color: 'var(--rule)' }} />
            <circle cx="18" cy="18" r="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ color: 'var(--c-kivo-teal)' }} strokeDasharray="22 66" />
          </svg>
        </div>
        <div style={{ font: `500 18px/1 var(--f-text)`, letterSpacing: '-0.01em' }}>
          <span style={{ color: 'var(--c-kivo-teal)' }}>kivo</span><span style={{ color: 'var(--ink)' }}>.skin</span>
        </div>
      </div>
    );
  }

  if (error && !photoConsent) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-6" style={{ background: 'var(--c-cream)' }}>
        <div className="max-w-sm w-full text-center">
          <div
            className="w-14 h-14 flex items-center justify-center mx-auto mb-5"
            style={{ borderRadius: 'var(--r-pill)', background: 'rgba(244,168,142,0.15)' }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--c-coral)' }}>
              <circle cx="12" cy="12" r="10" stroke="currentColor" />
              <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" />
              <line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" strokeWidth="2.5" />
            </svg>
          </div>
          <h3 style={{ font: `500 var(--fs-h3)/1.2 var(--f-display)`, color: 'var(--ink)', margin: '0 0 6px' }}>
            Something went wrong
          </h3>
          <p style={{ fontSize: 'var(--fs-small)', color: 'var(--ink-muted)', margin: '0 0 24px' }}>{error}</p>
          <Button onClick={() => { window.location.reload(); }}>Try again</Button>
        </div>
      </div>
    );
  }

  if (photoConsent && photoConsent.accepted && !photoConsent.needs_update) return <></>;
  if (typeof document === "undefined") return <></>;

  return createPortal(
    <AnimatePresence>
      {!success && (
        <motion.div
          className="fixed inset-0 z-[9999] flex items-center justify-center px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.25 } }}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0"
            style={{ background: 'var(--c-charcoal-60)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
          />

          <motion.div
            className="relative w-full max-w-sm overflow-hidden"
            style={{
              background: 'var(--c-cream)',
              borderRadius: 'var(--r-xl)',
              boxShadow: 'var(--sh-lifted)',
              border: '1px solid var(--rule)',
            }}
            initial={{ opacity: 0, scale: 0.92, y: 32 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 1.04, transition: { duration: 0.22 } }}
            transition={{ type: 'spring', stiffness: 420, damping: 36, mass: 0.9 }}
          >
            {/* Top accent line */}
            <div
              className="absolute inset-x-0 top-0 h-[2px] z-10 pointer-events-none"
              style={{ background: 'linear-gradient(to right, transparent, var(--c-kivo-teal), transparent)' }}
            />

            {/* Brand ambient orbs — teal top-right, coral bottom-left */}
            <div className="absolute top-0 right-0 w-56 h-56 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" style={{ background: 'var(--c-teal-weak)' }} />
            <div className="absolute bottom-0 left-0 w-44 h-44 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4 pointer-events-none" style={{ background: 'rgba(244,168,142,0.10)' }} />

            <div className="relative z-10 p-7 sm:p-8">
              {/* Biometric icon */}
              <div className="flex justify-center mb-6">
                <div className="relative">
                  <div
                    className="absolute inset-0 rounded-full animate-ping opacity-40"
                    style={{ animationDuration: '2.5s', background: 'var(--c-teal-weak)' }}
                  />
                  <div
                    className="relative w-16 h-16 rounded-full flex items-center justify-center"
                    style={{
                      background: 'var(--c-teal-weak)',
                      border: '1px solid rgba(42,164,166,0.25)',
                      color: 'var(--c-kivo-teal)',
                    }}
                  >
                    {/* Lucide Fingerprint */}
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 10a2 2 0 0 0-2 2c0 1.02-.1 2.51-.26 3.4" />
                      <path d="M14 13.12c0 2.38 0 6.38-1 8.88" />
                      <path d="M17.29 21.02c.12-.6.43-2.3.5-3.02" />
                      <path d="M2 12a10 10 0 0 1 18-6" />
                      <path d="M2 17c.5-2 1-4 1.1-5" />
                      <path d="M21.7 16.4c.1-.5.1-1.4.4-2.4" />
                      <path d="M6 10.71A10 10 0 0 1 22 10" />
                      <path d="M7.63 16.33A7 7 0 0 1 7 14" />
                      <path d="M12 14a2 2 0 0 1 2 2" />
                      <path d="M9.5 7.5A7 7 0 0 1 19 14" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Header */}
              <div className="text-center mb-6">
                <h2 style={{ font: `500 var(--fs-h3)/1.2 var(--f-display)`, color: 'var(--ink)', margin: '0 0 8px' }}>
                  Biometric Consent
                </h2>
                <p style={{ fontSize: 'var(--fs-small)', color: 'var(--ink-muted)', lineHeight: 1.6, margin: 0 }}>
                  To use face scanning, we need your consent to process biometric data under GDPR Article 9.
                </p>
                <p
                  role="note"
                  data-testid="ai-pre-analysis-notice"
                  style={{ fontSize: 'var(--fs-small)', color: 'var(--ink-muted)', lineHeight: 1.6, margin: '12px 0 0' }}
                >
                  {AI_PRE_ANALYSIS_NOTICE}
                </p>
              </div>

              {/* Consent summary */}
              <div
                className="mb-6 p-4"
                style={{
                  background: 'var(--c-teal-weak)',
                  borderRadius: 'var(--r-md)',
                  border: '1px solid rgba(42,164,166,0.2)',
                }}
              >
                <p style={{
                  fontSize: 'var(--fs-label)',
                  fontWeight: 500,
                  letterSpacing: 'var(--tracking-label)',
                  textTransform: 'uppercase',
                  color: 'var(--c-deep-teal)',
                  margin: '0 0 12px',
                }}>
                  What you agree to
                </p>
                <div className="space-y-2">
                  {CONSENT_ITEMS.map((item) => (
                    <div key={item} className="flex items-center gap-2">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--c-kivo-teal)', flexShrink: 0 }}>
                        <circle cx="12" cy="12" r="10" stroke="currentColor" />
                        <path d="M9 12l2 2 4-4" stroke="currentColor" />
                      </svg>
                      <span style={{ fontSize: 'var(--fs-small)', color: 'var(--ink)' }}>{item}</span>
                    </div>
                  ))}
                </div>
                <a
                  href="/photo-consent"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 mt-3"
                  style={{ fontSize: 'var(--fs-label)', fontWeight: 500, color: 'var(--c-kivo-teal)', letterSpacing: '0.04em' }}
                >
                  Read full policy
                  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6m4-3h6m0 0v6m0-6L10 14" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </a>
              </div>

              {error && (
                <div
                  className="mb-5 flex items-start gap-2 p-3"
                  style={{
                    background: 'rgba(244,168,142,0.12)',
                    border: '1px solid rgba(244,168,142,0.3)',
                    borderRadius: 'var(--r-sm)',
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5" style={{ color: 'var(--c-coral)' }}>
                    <circle cx="12" cy="12" r="10" stroke="currentColor" />
                    <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" />
                    <line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" strokeWidth="2.5" />
                  </svg>
                  <p style={{ fontSize: 'var(--fs-small)', color: 'var(--ink)', margin: 0 }}>{error}</p>
                </div>
              )}

              {/* Checkboxes */}
              <div className="space-y-3.5 mb-5">
                <ConsentCheckbox
                  id="photo-consent-accepted"
                  checked={accepted}
                  onChange={(e) => { setAccepted(e.target.checked); }}
                >
                  I consent to photo processing as described in the{' '}
                  <a
                    href="/photo-consent"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--c-kivo-teal)', textDecoration: 'underline', textUnderlineOffset: '2px' }}
                  >
                    Photo Consent Policy
                  </a>
                </ConsentCheckbox>

                <ConsentCheckbox
                  id="photo-consent-ai"
                  checked={aiConsent}
                  onChange={(e) => { setAiConsent(e.target.checked); }}
                >
                  Allow my photos to improve the AI{' '}
                  <span style={{ color: 'var(--ink-muted)', fontSize: 'var(--fs-label)' }}>(optional)</span>
                  <span style={{ display: 'block', color: 'var(--ink-muted)', fontSize: 'var(--fs-label)', marginTop: '2px' }}>
                    Used only to improve our own models, kept no longer than needed, and you can withdraw any time. See our{' '}
                    <a
                      href="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'var(--c-kivo-teal)', textDecoration: 'underline', textUnderlineOffset: '2px' }}
                    >
                      Privacy Policy
                    </a>.
                  </span>
                </ConsentCheckbox>
              </div>

              {/* CTA */}
              <Button
                onClick={handleAccept}
                disabled={!accepted || submitting}
                className={`w-full ${!accepted ? 'opacity-40' : ''}`}
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Processing...
                  </span>
                ) : (
                  'Accept & Continue'
                )}
              </Button>

              <button
                onClick={() => { navigate("/"); }}
                className="w-full mt-3 py-2"
                style={{
                  fontSize: 'var(--fs-small)',
                  color: 'var(--ink-muted)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  transition: `color var(--t-fast) var(--ease)`,
                }}
              >
                Not now, go back
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
